import React, { useEffect, useState } from 'react';
import {
  collection, doc, onSnapshot, runTransaction, serverTimestamp, updateDoc,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useLang } from '../LangContext';
import { AccessDenied, canAccess } from '../adminAccess';
import Table from '../components/Table';
import { buildLocalizedTextPayload } from '../localizedTextPayload';
import {
  buildFlowerImagePath, isSupportedFlowerImageType, resolveFlowerImageOnSave,
} from '../flowerImageUtils';

// Shown as the file picker's `accept` filter AND enforced again in
// uploadFlowerImage() -- `accept` is only a UI hint a user can bypass
// (drag-drop, "All files"), so the real gate is the type check before
// upload, not this attribute alone.
const FLOWER_IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';

/** Uploads one flower image to Firebase Storage and returns its
 * {imageUrl, imagePath} -- same shape and same "upload first, write the
 * Firestore field second" ordering as CemeteriesPage.js's cemetery
 * thumbnail upload, so a failed upload never touches the flower document
 * and the flower's current working image is never at risk. Binary bytes
 * live in Storage only; the Firestore document only ever stores the
 * resulting download URL + path, never base64 image data. */
async function uploadFlowerImage(entryId, file) {
  // Defense in depth: the file pickers already filter by `accept` and
  // reject an unsupported pick before it ever reaches here, but `accept`
  // alone is only a UI hint a user can bypass -- this is the real gate.
  if (!isSupportedFlowerImageType(file.type)) {
    throw new Error('Unsupported image format.');
  }
  const imagePath = buildFlowerImagePath(entryId, file.name);
  const snapshot = await uploadBytes(ref(storage, imagePath), file, { contentType: file.type });
  try {
    const imageUrl = await getDownloadURL(snapshot.ref);
    return { imageUrl, imagePath };
  } catch (err) {
    // The bytes uploaded fine, but a usable {imageUrl, imagePath} can never
    // be handed back to the caller for this attempt (Codex finding) -- the
    // caller's own uploadedThisAttempt tracking only starts AFTER this
    // function returns successfully, so without this cleanup the just-
    // uploaded object would be permanently orphaned, and every retry would
    // leave another one behind.
    await deleteFlowerImage(imagePath);
    throw err;
  }
}

/** Deletes a flower's OLD Storage object, best-effort, only ever by its own
 * previously-owned imagePath -- never another flower's. A failure here
 * (already deleted, transient network error) must never undo an already-
 * successful Firestore save, so it's swallowed, same as the cemetery
 * thumbnail cleanup this mirrors. */
async function deleteFlowerImage(imagePath) {
  if (!imagePath) return;
  try {
    await deleteObject(ref(storage, imagePath));
  } catch (_) {
    // The flower document is already safely saved; a stale/already-gone
    // Storage object is not worth surfacing to the admin.
  }
}

const labelsByLang = {
  en: {
    title: 'Flower Catalog',
    description: 'The live, admin-managed flower/arrangement catalog customers choose from after picking a service package. A new entry appears in the customer app immediately, with no app update. Existing orders keep the flower details they were created with, unaffected by any later edit.',
    nameAz: 'Name (AZ)',
    nameRu: 'Name (RU)',
    nameEn: 'Name (EN)',
    priceType: 'Price type',
    fixed: 'Fixed price',
    from: 'Starting price ("from") -- quoted before payment',
    price: 'Price (₼)',
    descAz: 'Description (AZ)',
    descRu: 'Description (RU)',
    descEn: 'Description (EN)',
    create: 'Create entry',
    creating: 'Creating…',
    status: 'Status',
    active: 'Active',
    inactive: 'Deactivated',
    deactivate: 'Deactivate',
    reactivate: 'Reactivate',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    noData: 'No flower entries yet',
    errNameRequired: 'Enter a name in all three languages.',
    errPriceInvalid: 'Enter a price greater than 0.',
    errExists: 'A flower entry with this AZ name already exists.',
    confirmDeactivate: 'Deactivate this entry? Customers will no longer be able to select it for a new order.',
    seedDefaults: 'Seed default flowers',
    seeding: 'Seeding…',
    seedDone: (created, skipped) => `Seeded ${created} new entr${created === 1 ? 'y' : 'ies'}${skipped ? `, skipped ${skipped} already present` : ''}.`,
    entry: 'Name',
    priceCol: 'Price',
    image: 'Image (shared across AZ/RU/EN)',
    chooseImage: 'Choose image',
    replaceImage: 'Replace image',
    removeImage: 'Remove image',
    confirmRemoveImage: 'Remove this flower\'s image? Save to apply.',
    noImage: 'No image',
    errUnsupportedImageType: 'Unsupported image format. Use JPG, PNG, WEBP, or GIF.',
  },
  az: {
    title: 'Gül Kataloqu',
    description: 'Müştərilərin xidmət paketini seçdikdən sonra seçdiyi, canlı, admin idarə edilən gül/kompozisiya kataloqu. Yeni əlavə heç bir tətbiq yeniləməsi olmadan müştəri tətbiqində dərhal görünür. Mövcud sifarişlər yaradıldığı andakı gül məlumatlarını saxlayır, sonrakı heç bir redaktədən təsirlənmir.',
    nameAz: 'Ad (AZ)',
    nameRu: 'Ad (RU)',
    nameEn: 'Ad (EN)',
    priceType: 'Qiymət növü',
    fixed: 'Sabit qiymət',
    from: 'Başlanğıc qiymət ("başlayaraq") -- ödənişdən əvvəl təsdiqlənir',
    price: 'Qiymət (₼)',
    descAz: 'Təsvir (AZ)',
    descRu: 'Təsvir (RU)',
    descEn: 'Təsvir (EN)',
    create: 'Yeni əlavə et',
    creating: 'Yaradılır…',
    status: 'Status',
    active: 'Aktiv',
    inactive: 'Deaktiv',
    deactivate: 'Deaktiv et',
    reactivate: 'Aktivləşdir',
    edit: 'Redaktə et',
    save: 'Yadda saxla',
    cancel: 'Ləğv et',
    noData: 'Hələ gül əlavəsi yoxdur',
    errNameRequired: 'Hər üç dildə ad daxil edin.',
    errPriceInvalid: '0-dan böyük qiymət daxil edin.',
    errExists: 'Bu AZ adı ilə gül əlavəsi artıq mövcuddur.',
    confirmDeactivate: 'Bu əlavə deaktiv edilsin? Müştərilər onu yeni sifariş üçün seçə bilməyəcək.',
    seedDefaults: 'Standart gülləri yarat',
    seeding: 'Yaradılır…',
    seedDone: (created, skipped) => `${created} yeni əlavə yaradıldı${skipped ? `, ${skipped} onsuz da mövcud idi` : ''}.`,
    entry: 'Ad',
    priceCol: 'Qiymət',
    image: 'Şəkil (AZ/RU/EN üçün ortaq)',
    chooseImage: 'Şəkil seç',
    replaceImage: 'Şəkli dəyiş',
    removeImage: 'Şəkli sil',
    confirmRemoveImage: 'Bu gülün şəkli silinsin? Tətbiq etmək üçün yadda saxlayın.',
    noImage: 'Şəkil yoxdur',
    errUnsupportedImageType: 'Dəstəklənməyən şəkil formatı. JPG, PNG, WEBP və ya GIF istifadə edin.',
  },
  ru: {
    title: 'Каталог цветов',
    description: 'Живой каталог цветов/композиций, управляемый администратором, из которого клиенты выбирают после выбора пакета услуг. Новая запись появляется в приложении клиента немедленно, без обновления приложения. Существующие заказы сохраняют данные о цветах на момент создания и не затрагиваются последующим редактированием.',
    nameAz: 'Название (AZ)',
    nameRu: 'Название (RU)',
    nameEn: 'Название (EN)',
    priceType: 'Тип цены',
    fixed: 'Фиксированная цена',
    from: 'Начальная цена ("от") -- подтверждается до оплаты',
    price: 'Цена (₼)',
    descAz: 'Описание (AZ)',
    descRu: 'Описание (RU)',
    descEn: 'Описание (EN)',
    create: 'Создать запись',
    creating: 'Создание…',
    status: 'Статус',
    active: 'Активен',
    inactive: 'Деактивирован',
    deactivate: 'Деактивировать',
    reactivate: 'Активировать',
    edit: 'Редактировать',
    save: 'Сохранить',
    cancel: 'Отмена',
    noData: 'Пока нет записей о цветах',
    errNameRequired: 'Введите название на всех трёх языках.',
    errPriceInvalid: 'Введите цену больше 0.',
    errExists: 'Запись о цветах с этим AZ-названием уже существует.',
    confirmDeactivate: 'Деактивировать эту запись? Клиенты больше не смогут выбрать её для нового заказа.',
    seedDefaults: 'Создать стандартные цветы',
    seeding: 'Создание…',
    seedDone: (created, skipped) => `Создано ${created} новых записей${skipped ? `, пропущено ${skipped} уже существующих` : ''}.`,
    entry: 'Название',
    priceCol: 'Цена',
    image: 'Изображение (общее для AZ/RU/EN)',
    chooseImage: 'Выбрать изображение',
    replaceImage: 'Заменить изображение',
    removeImage: 'Удалить изображение',
    confirmRemoveImage: 'Удалить изображение этого цветка? Сохраните, чтобы применить.',
    noImage: 'Нет изображения',
    errUnsupportedImageType: 'Неподдерживаемый формат изображения. Используйте JPG, PNG, WEBP или GIF.',
  },
};

/** Deterministic, collision-safe doc id from the AZ name -- the same name
 * always resolves to the same document, so a repeat "create" cleanly fails
 * as a duplicate instead of silently making a second, confusing row. Same
 * percent-encoding approach as service_packages' entryDocId(), which fixed
 * a collision class a plain char-stripping slug had (Codex finding). */
function entryDocId(nameAz) {
  const id = encodeURIComponent(String(nameAz || '').trim().toLowerCase());
  // A fixed, ALWAYS-applied prefix -- not a conditional guard -- sidesteps
  // every Firestore doc-id restriction encodeURIComponent leaves untouched:
  // the reserved __.*__ pattern, and the bare "." / ".." ids Firestore
  // rejects outright. The previous conditional-only guard let two distinct
  // names collide on the same guarded id (e.g. "__rose__" and "rose" both
  // resolving to "x__rose__") -- Codex finding, round 1.
  return `flower_${id}`;
}

/** The 5 flowers flowerAddOnCatalog.dart currently ships with, carried over
 * verbatim (same prices, same "large_memorial_arrangement" quoted-price
 * behaviour) so nothing a customer currently sees disappears on cutover.
 * "No flowers" is NOT seeded -- it's synthetic in the app, never a document
 * here. */
const SEED_ROWS = [
  {
    name: { az: 'Kiçik anım buketi', ru: 'Небольшой памятный букет', en: 'Small memorial bouquet' },
    price: 45, priceType: 'fixed',
  },
  {
    name: { az: 'Standart təzə kompozisiya', ru: 'Стандартная свежая композиция', en: 'Standard fresh arrangement' },
    price: 70, priceType: 'fixed',
  },
  {
    name: { az: 'Premium anım kompozisiyası', ru: 'Премиум памятная композиция', en: 'Premium memorial arrangement' },
    price: 110, priceType: 'fixed',
  },
  {
    name: { az: 'Böyük anım kompozisiyası', ru: 'Большая памятная композиция', en: 'Large memorial arrangement' },
    price: 160, priceType: 'from',
  },
];

export default function FlowerCatalogPage({ user }) {
  const { lang } = useLang();
  const labels = labelsByLang[lang] || labelsByLang.en;

  const [entries, setEntries] = useState([]);

  const [nameAz, setNameAz] = useState('');
  const [nameRu, setNameRu] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [priceType, setPriceType] = useState('fixed');
  const [price, setPrice] = useState('');
  const [descAz, setDescAz] = useState('');
  const [descRu, setDescRu] = useState('');
  const [descEn, setDescEn] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  // Bumped after a successful create to force the uncontrolled file input
  // to remount -- clearing imageFile/imagePreview alone leaves the native
  // <input> still showing the previously selected filename.
  const [imageInputKey, setImageInputKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  // Editing an existing entry's price/priceType/description/image in place.
  // `name` is the doc-id-determining identity field and stays immutable
  // once created (same pattern as service_packages' cityId/graveSize/
  // careType) -- to rename a flower, deactivate this entry and create a
  // new one.
  const [editingId, setEditingId] = useState(null);
  const [editPriceType, setEditPriceType] = useState('fixed');
  const [editPrice, setEditPrice] = useState('');
  const [editDescAz, setEditDescAz] = useState('');
  const [editDescRu, setEditDescRu] = useState('');
  const [editDescEn, setEditDescEn] = useState('');
  // editImageFile: a newly-picked replacement, not yet uploaded.
  // editImagePreview: what to show -- the new file's local preview, the
  // entry's current imageUrl, or '' once removePendingImage() has run.
  // editImageRemoved: explicit "remove on save" flag, independent of
  // editImageFile so picking a new file after removing cleanly overrides
  // the removal instead of fighting over which one wins.
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState('');
  const [editImageRemoved, setEditImageRemoved] = useState(false);
  // Bumped whenever the pending pick is cleared (remove, or startEdit on a
  // new row) so the uncontrolled file input remounts -- otherwise it keeps
  // showing the just-picked filename, and re-selecting that SAME file to
  // replace-instead-of-remove would not fire onChange (Codex finding).
  const [editImageInputKey, setEditImageInputKey] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const stop = onSnapshot(
      collection(db, 'flower_catalog'),
      snap => setEntries(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
      ),
      () => setError('Unable to load the flower catalog. Please refresh or sign in again.'),
    );
    return () => stop();
  }, []);

  if (!canAccess(user, 'flowers')) return <AccessDenied />;

  async function createEntry(e) {
    e.preventDefault();
    setError('');
    setMsg('');

    const az = nameAz.trim();
    const ru = nameRu.trim();
    const en = nameEn.trim();
    if (!az || !ru || !en) { setError(labels.errNameRequired); return; }
    const value = Number(price);
    if (!Number.isFinite(value) || value <= 0) { setError(labels.errPriceInvalid); return; }

    setCreating(true);
    // Tracks whether THIS call uploaded a brand-new image, so a subsequent
    // failure (e.g. the duplicate-name transaction below) can clean up
    // that orphaned object -- Codex finding: previously a failed create
    // left the just-uploaded file permanently unreferenced, and every
    // retry uploaded yet another one.
    let uploadedThisAttempt = '';
    try {
      const id = entryDocId(az);
      // Uploaded (if provided) BEFORE the Firestore write, same ordering as
      // CemeteriesPage.js's cemetery thumbnail: a failed upload throws here
      // and the transaction below never runs, so no half-created entry is
      // ever left with a broken image reference.
      let imageUrl = '';
      let imagePath = '';
      if (imageFile) {
        const uploaded = await uploadFlowerImage(id, imageFile);
        imageUrl = uploaded.imageUrl;
        imagePath = uploaded.imagePath;
        uploadedThisAttempt = imagePath;
      }
      const docRef = doc(db, 'flower_catalog', id);
      // A transaction, not a plain setDoc: two admins creating the same
      // flower name at once must never silently clobber each other (same
      // TOCTOU-safe pattern as service_packages/promo_codes).
      await runTransaction(db, async tx => {
        const existing = await tx.get(docRef);
        if (existing.exists()) throw new Error(labels.errExists);
        tx.set(docRef, {
          name: { az, ru, en },
          price: value,
          priceType,
          description: buildLocalizedTextPayload(descAz, descRu, descEn),
          imageUrl,
          imagePath,
          active: true,
          sortOrder: entries.length,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });
      setNameAz(''); setNameRu(''); setNameEn('');
      setPriceType('fixed'); setPrice('');
      setDescAz(''); setDescRu(''); setDescEn('');
      setImageFile(null); setImagePreview('');
      // Uncontrolled <input type="file"> keeps its native selection even
      // after React state is cleared -- remounting it via a changed `key`
      // is what actually clears the displayed filename (Codex finding).
      setImageInputKey(k => k + 1);
    } catch (err) {
      setError(err.message || String(err));
      // The entry was never created (or a different failure happened after
      // upload) -- a file we JUST uploaded this attempt is now orphaned,
      // never referenced by any document. Never touches any other image.
      if (uploadedThisAttempt) {
        await deleteFlowerImage(uploadedThisAttempt);
      }
    } finally {
      setCreating(false);
    }
  }

  async function toggleActive(entry) {
    const willDeactivate = entry.active === true;
    if (willDeactivate && !window.confirm(labels.confirmDeactivate)) return;
    try {
      await updateDoc(doc(db, 'flower_catalog', entry.id), {
        active: !willDeactivate,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      setError(err.message || String(err));
    }
  }

  function startEdit(entry) {
    setError(''); setMsg('');
    setEditingId(entry.id);
    setEditPriceType(entry.priceType === 'from' ? 'from' : 'fixed');
    setEditPrice(String(entry.price ?? ''));
    setEditDescAz(entry.description?.az || '');
    setEditDescRu(entry.description?.ru || '');
    setEditDescEn(entry.description?.en || '');
    setEditImageFile(null);
    setEditImagePreview(entry.imageUrl || '');
    setEditImageRemoved(false);
    setEditImageInputKey(k => k + 1);
  }

  function cancelEdit() {
    setEditingId(null);
  }

  /** Marks the current image for removal on save -- does not touch Storage
   * yet, exactly like removePhoto() in CemeteriesPage.js: the actual
   * deletion only happens after saveEdit()'s Firestore update succeeds. */
  function removeEditImage(labelsForConfirm) {
    if (!window.confirm(labelsForConfirm.confirmRemoveImage)) return;
    setEditImageFile(null);
    setEditImagePreview('');
    setEditImageRemoved(true);
    // Remounts the file input -- without this, re-picking the SAME file
    // the admin just removed (meaning "replace with this one after all")
    // would not fire onChange, and the save would incorrectly remove the
    // image instead of applying the intended replacement (Codex finding).
    setEditImageInputKey(k => k + 1);
  }

  async function saveEdit(entry) {
    setError('');
    const value = Number(editPrice);
    if (!Number.isFinite(value) || value <= 0) { setError(labels.errPriceInvalid); return; }
    setSaving(true);
    // Tracks a brand-new upload from THIS save, so a subsequent write
    // failure can clean it up without touching the entry's previous image.
    let uploadedThisAttempt = '';
    try {
      // 1. Upload the new image first (if any) -- a failed upload throws
      //    here, before the flower document or the old image are touched,
      //    so the currently-working image/data is always preserved on
      //    failure.
      let uploaded = null;
      if (editImageFile) {
        uploaded = await uploadFlowerImage(entry.id, editImageFile);
        uploadedThisAttempt = uploaded.imagePath;
      }
      // 2. Safely update the flower document. Price/description-only saves
      //    (no image touched) OMIT imageUrl/imagePath entirely rather than
      //    writing back this snapshot's `entry.imageUrl`/`imagePath` --
      //    Codex finding: two admins editing the same flower concurrently
      //    could otherwise have a price-only save silently revert (and,
      //    combined with step 3, even delete) an image the OTHER admin had
      //    just replaced. Omitting the fields leaves Firestore's current
      //    value untouched no matter how stale this snapshot is.
      //
      //    When the image itself IS changing (replace or remove), omitting
      //    the fields isn't an option -- but writing back based on `entry`'s
      //    own possibly-stale imagePath has the exact same race: a losing
      //    admin's remove/replace, computed against a stale snapshot, could
      //    silently clobber a winning admin's just-uploaded image reference
      //    with zero error to either (adversarial review finding, 2026-10-
      //    01 final integration candidate -- same root cause as the Codex
      //    finding above, just not extended to this branch). Fixed the same
      //    way createEntry() above already handles concurrent creates: a
      //    transaction that reads the LIVE document first, so the image
      //    decision and oldPathToDelete are computed from current Firestore
      //    state, never a stale prop.
      const updatePayload = {
        price: value,
        priceType: editPriceType,
        description: buildLocalizedTextPayload(editDescAz, editDescRu, editDescEn),
        updatedAt: serverTimestamp(),
      };
      const imageChanged = !!editImageFile || editImageRemoved;
      const docRef = doc(db, 'flower_catalog', entry.id);
      let oldPathToDelete = '';
      await runTransaction(db, async tx => {
        if (imageChanged) {
          const live = await tx.get(docRef);
          const liveData = live.exists() ? live.data() : {};
          const resolved = resolveFlowerImageOnSave({
            currentImageUrl: liveData.imageUrl || '',
            currentImagePath: liveData.imagePath || '',
            hasNewFile: !!editImageFile,
            uploadedImageUrl: uploaded?.imageUrl,
            uploadedImagePath: uploaded?.imagePath,
            removed: editImageRemoved,
          });
          updatePayload.imageUrl = resolved.imageUrl;
          updatePayload.imagePath = resolved.imagePath;
          oldPathToDelete = resolved.oldPathToDelete;
        }
        tx.update(docRef, updatePayload);
      });
      // 3. Only now, after the transaction has actually committed against
      //    the live document, clean up the OLD image -- never another
      //    flower's Storage object, and never a concurrently-replaced one,
      //    since oldPathToDelete was computed from the live read above, not
      //    a stale snapshot.
      if (oldPathToDelete) {
        await deleteFlowerImage(oldPathToDelete);
      }
      // Functional update: if the admin opened a DIFFERENT row's editor
      // while this save was still in flight, editingId has already moved
      // on, and unconditionally clearing it would silently close that
      // other row's unsaved editor (same race already fixed once in
      // service_packages).
      setEditingId(current => (current === entry.id ? null : current));
    } catch (err) {
      setError(err.message || String(err));
      // The Firestore write never succeeded (or failed after it) -- a file
      // uploaded THIS attempt is orphaned; the entry's previous, still-
      // working image (entry.imagePath) is never touched here.
      if (uploadedThisAttempt) {
        await deleteFlowerImage(uploadedThisAttempt);
      }
    } finally {
      setSaving(false);
    }
  }

  /** Idempotent: creates only entries that don't already exist at their
   * deterministic id, so a repeat click never duplicates or overwrites an
   * admin's later price edit. */
  async function seedDefaults() {
    setError(''); setMsg('');
    setSeeding(true);
    try {
      let created = 0;
      let skipped = 0;
      for (let index = 0; index < SEED_ROWS.length; index += 1) {
        const row = SEED_ROWS[index];
        const id = entryDocId(row.name.az);
        const ref = doc(db, 'flower_catalog', id);
        const wasCreated = await runTransaction(db, async tx => {
          const existing = await tx.get(ref);
          if (existing.exists()) return false;
          tx.set(ref, {
            name: row.name,
            price: row.price,
            priceType: row.priceType,
            description: {},
            imageUrl: '',
            imagePath: '',
            active: true,
            sortOrder: index,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          return true;
        });
        if (wasCreated) created += 1; else skipped += 1;
      }
      setMsg(labels.seedDone(created, skipped));
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSeeding(false);
    }
  }

  return (
    <div className="page-shell">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="page-title">{labels.title}</h1>
          <p className="page-description max-w-2xl">{labels.description}</p>
        </div>
        <button onClick={seedDefaults} disabled={seeding} className="btn-secondary whitespace-nowrap">
          {seeding ? labels.seeding : labels.seedDefaults}
        </button>
      </div>

      <form onSubmit={createEntry} className="rounded-xl border border-gray-100 bg-white p-4 mb-6 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.nameAz}</label>
          <input className="input-field w-full" value={nameAz} onChange={e => setNameAz(e.target.value)} />
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.nameRu}</label>
          <input className="input-field w-full" value={nameRu} onChange={e => setNameRu(e.target.value)} />
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.nameEn}</label>
          <input className="input-field w-full" value={nameEn} onChange={e => setNameEn(e.target.value)} />
        </div>
        <div className="w-56">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.priceType}</label>
          <select className="input-field w-full" value={priceType} onChange={e => setPriceType(e.target.value)}>
            <option value="fixed">{labels.fixed}</option>
            <option value="from">{labels.from}</option>
          </select>
        </div>
        <div className="w-28">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.price}</label>
          <input className="input-field w-full" type="number" min="0" step="0.01" value={price} onChange={e => setPrice(e.target.value)} />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.descAz}</label>
          <textarea className="input-field w-full" rows="3" value={descAz} onChange={e => setDescAz(e.target.value)} />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.descRu}</label>
          <textarea className="input-field w-full" rows="3" value={descRu} onChange={e => setDescRu(e.target.value)} />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.descEn}</label>
          <textarea className="input-field w-full" rows="3" value={descEn} onChange={e => setDescEn(e.target.value)} />
        </div>
        <div className="min-w-[160px]">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.image}</label>
          {imagePreview && <img src={imagePreview} alt="" className="mb-2 h-16 w-16 rounded-lg object-cover" />}
          <input
            key={imageInputKey}
            type="file"
            accept={FLOWER_IMAGE_ACCEPT}
            onChange={e => {
              const next = e.target.files?.[0];
              if (next && !isSupportedFlowerImageType(next.type)) {
                setError(labels.errUnsupportedImageType);
                setImageInputKey(k => k + 1);
                return;
              }
              setError('');
              setImageFile(next || null);
              setImagePreview(next ? URL.createObjectURL(next) : '');
            }}
          />
        </div>
        <button type="submit" disabled={creating} className="btn-primary whitespace-nowrap">
          {creating ? labels.creating : labels.create}
        </button>
      </form>

      {msg && <p className="text-green-600 text-sm mb-4">{msg}</p>}
      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

      <Table headers={[labels.entry, labels.priceCol, labels.status, '']} empty={labels.noData}>
        {entries.map(entry => {
          const isActive = entry.active === true;
          if (entry.id === editingId) {
            return (
              <tr key={entry.id} className="border-b border-gray-50 bg-gray-50 text-sm align-top">
                <td className="px-4 py-3" colSpan={4}>
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="font-medium">{entry.name?.[lang] || entry.name?.az}</div>
                    <div className="w-56">
                      <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.priceType}</label>
                      <select className="input-field w-full" value={editPriceType} onChange={e => setEditPriceType(e.target.value)}>
                        <option value="fixed">{labels.fixed}</option>
                        <option value="from">{labels.from}</option>
                      </select>
                    </div>
                    <div className="w-28">
                      <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.price}</label>
                      <input className="input-field w-full" type="number" min="0" step="0.01" value={editPrice} onChange={e => setEditPrice(e.target.value)} />
                    </div>
                    <div className="flex-1 min-w-[140px]">
                      <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.descAz}</label>
                      <textarea className="input-field w-full" rows="3" value={editDescAz} onChange={e => setEditDescAz(e.target.value)} />
                    </div>
                    <div className="flex-1 min-w-[140px]">
                      <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.descRu}</label>
                      <textarea className="input-field w-full" rows="3" value={editDescRu} onChange={e => setEditDescRu(e.target.value)} />
                    </div>
                    <div className="flex-1 min-w-[140px]">
                      <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.descEn}</label>
                      <textarea className="input-field w-full" rows="3" value={editDescEn} onChange={e => setEditDescEn(e.target.value)} />
                    </div>
                    <div className="min-w-[160px]">
                      <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.image}</label>
                      {editImagePreview
                        ? <img src={editImagePreview} alt="" className="mb-2 h-16 w-16 rounded-lg object-cover" />
                        : <div className="mb-2 flex h-16 w-16 items-center justify-center rounded-lg bg-gray-100 text-[10px] text-gray-400">{labels.noImage}</div>}
                      <input
                        key={editImageInputKey}
                        type="file"
                        accept={FLOWER_IMAGE_ACCEPT}
                        onChange={e => {
                          const next = e.target.files?.[0];
                          if (next && !isSupportedFlowerImageType(next.type)) {
                            setError(labels.errUnsupportedImageType);
                            setEditImageInputKey(k => k + 1);
                            return;
                          }
                          setError('');
                          setEditImageFile(next || null);
                          setEditImageRemoved(false);
                          if (next) setEditImagePreview(URL.createObjectURL(next));
                        }}
                      />
                      {editImagePreview && (
                        <button type="button" className="ml-2 text-xs text-red-600" onClick={() => removeEditImage(labels)}>
                          {labels.removeImage}
                        </button>
                      )}
                    </div>
                    <button onClick={() => saveEdit(entry)} disabled={saving} className="btn-primary whitespace-nowrap">
                      {saving ? labels.creating : labels.save}
                    </button>
                    <button onClick={cancelEdit} className="btn-secondary whitespace-nowrap">{labels.cancel}</button>
                  </div>
                </td>
              </tr>
            );
          }
          return (
            <tr key={entry.id} className="border-b border-gray-50 hover:bg-gray-50 text-sm align-top">
              <td className="px-4 py-3">
                <div className="flex items-start gap-3">
                  {entry.imageUrl
                    ? <img src={entry.imageUrl} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />
                    : <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-300">🌸</div>}
                  <div>
                    <div className="font-medium">{entry.name?.[lang] || entry.name?.az}</div>
                    {entry.description?.[lang] && (
                      <div className="text-gray-500 text-xs mt-1 max-w-md whitespace-pre-line">{entry.description[lang]}</div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 font-bold text-primary whitespace-nowrap">
                {entry.priceType === 'from' ? `${labels.from.split(' ')[0]} ` : ''}{entry.price} ₼
              </td>
              <td className="px-4 py-3">
                <span className={`badge ${isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {isActive ? labels.active : labels.inactive}
                </span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <button
                  onClick={() => startEdit(entry)}
                  className="text-xs font-semibold text-primary hover:underline mr-3"
                >
                  {labels.edit}
                </button>
                <button
                  onClick={() => toggleActive(entry)}
                  className={`text-xs font-semibold ${isActive ? 'text-red-600 hover:text-red-700' : 'text-emerald-600 hover:text-emerald-700'}`}
                >
                  {isActive ? labels.deactivate : labels.reactivate}
                </button>
              </td>
            </tr>
          );
        })}
      </Table>
    </div>
  );
}
